import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Tabs, Tab, CircularProgress, Switch, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { bonusService } from 'components/api/bonuses';
import { commonGridStyles } from 'styles/gridStyles';
import styles from './BonusSettings.module.scss';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import { Edit } from 'lucide-react';
import EditBonusSettingModal from './EditBonusSettingModal';
import { siteService } from 'components/api/sites';

interface BonusSettingsProps {
  siteId: string | number;
}

interface TierBonusSetting {
  id: number;
  tier: string;
  tierName?: string;
  index?: number;
  bonusType: string;
  enabled: boolean;
  percent: string;
  minAmount: number | null;
  maxAmount: number | null;
  rolloverMultiplier: string;
  periodType: string;
  resetDay: number | null;
  freeSpinCount: number;
  config: any;
}

const TIER_ORDER = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'nebula'];

const BonusSettings: React.FC<BonusSettingsProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<Record<string, TierBonusSetting[]>>({});
  const [levelSettings, setLevelSettings] = useState<any[]>([]);
  const [activeTier, setActiveTier] = useState<string>('');
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<TierBonusSetting | null>(null);
  const [savingSetting, setSavingSetting] = useState(false);

  const handleEditClick = (setting: TierBonusSetting) => {
    setEditingSetting(setting);
    setIsEditOpen(true);
  };

  const handleSaveEdit = async (payload: any) => {
    if (!editingSetting) return;
    setSavingSetting(true);
    try {
      const fullPayload = {
        ...payload,
        siteId: Number(siteId),
        tier: editingSetting.tier,
        bonusType: editingSetting.bonusType,
        enabled: editingSetting.enabled,
      };

      await bonusService.updateLevelBonusSetting(fullPayload);

      setTiers((prev) => {
        const nextState = { ...prev };
        if (nextState[activeTier]) {
          nextState[activeTier] = nextState[activeTier].map((item) =>
            item.id === editingSetting.id ? { ...item, ...payload } : item,
          );
        }
        return nextState;
      });

      dispatch(showToast({ text: 'Setting updated successfully', type: 'success' }));
      setIsEditOpen(false);
      setEditingSetting(null);
    } catch (error: any) {
      console.error('Failed to update level bonus setting:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to update setting',
          type: 'error',
        }),
      );
    } finally {
      setSavingSetting(false);
    }
  };

  const handleToggleSetting = async (id: number, currentEnabled: boolean) => {
    const nextEnabled = !currentEnabled;
    setTogglingId(id);
    try {
      await bonusService.toggleLevelBonusSetting(id, nextEnabled);
      setTiers((prev) => {
        const nextState = { ...prev };
        if (nextState[activeTier]) {
          nextState[activeTier] = nextState[activeTier].map((item) =>
            item.id === id ? { ...item, enabled: nextEnabled } : item,
          );
        }
        return nextState;
      });
      dispatch(showToast({ text: 'Setting status updated successfully', type: 'success' }));
    } catch (error: any) {
      console.error('Failed to toggle level bonus setting:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to update setting',
          type: 'error',
        }),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [response, levelRes] = await Promise.all([
        bonusService.getLevelBonusSettings(siteId),
        siteService.getLevelSettings(siteId),
      ]);
      const data = response?.data?.tiers || {};
      const levels = levelRes?.data?.levelSettings || [];

      setTiers(data);
      setLevelSettings(levels);

      const uniqueTiersMap = new Map<string, number>();
      levels.forEach((lvl: any) => {
        if (!uniqueTiersMap.has(lvl.tier)) {
          uniqueTiersMap.set(lvl.tier, lvl.index);
        } else {
          if (lvl.index < uniqueTiersMap.get(lvl.tier)!) {
            uniqueTiersMap.set(lvl.tier, lvl.index);
          }
        }
      });

      const sortedKeys = Object.keys(data).sort((a, b) => {
        const indexA = uniqueTiersMap.get(a.toLowerCase()) ?? 999;
        const indexB = uniqueTiersMap.get(b.toLowerCase()) ?? 999;
        return indexA - indexB;
      });
      if (sortedKeys.length > 0) {
        setActiveTier(sortedKeys[0]);
      }
    } catch (error: any) {
      console.error('Failed to fetch level bonus settings:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to fetch bonus settings',
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [siteId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTier(newValue);
  };

  const formatBonusType = (type: string) => {
    if (!type) return '';
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'bonusType',
        headerName: 'Bonus Type',
        width: 100,
        valueGetter: (value: any) => formatBonusType(value),
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        renderCell: (params: any) => (
          <Switch
            checked={params.row.enabled ?? false}
            disabled={togglingId === params.row.id}
            onChange={() => handleToggleSetting(params.row.id, params.row.enabled)}
            size="small"
          />
        ),
      },
      {
        field: 'percent',
        headerName: 'Percent (%)',
        width: 110,
        valueFormatter: (value: any) => `${value || '0'}%`,
      },
      {
        field: 'minAmount',
        headerName: 'Min Amount',
        width: 130,
        valueFormatter: (value: any) => (value !== null && value !== undefined ? `$${value}` : '-'),
      },
      {
        field: 'maxAmount',
        headerName: 'Max Amount',
        width: 130,
        valueFormatter: (value: any) => (value !== null && value !== undefined ? `$${value}` : '-'),
      },
      {
        field: 'rolloverMultiplier',
        headerName: 'Rollover',
        width: 100,
        valueFormatter: (value: any) => `${value || '0'}x`,
      },
      {
        field: 'periodType',
        headerName: 'Period',
        width: 120,
        valueGetter: (value: any) => (value ? String(value).toUpperCase() : 'NONE'),
      },
      {
        field: 'resetDay',
        headerName: 'Reset Day',
        width: 100,
        valueFormatter: (value: any) => (value !== null && value !== undefined ? value : '-'),
      },
      {
        field: 'freeSpinCount',
        headerName: 'Free Spins',
        width: 120,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              sx={{ minWidth: '40px' }}
              variant="contained"
              size="small"
              startIcon={<Edit size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params.row);
              }}
            >
              Edit
            </Button>
          </Box>
        ),
      },
    ],
    [togglingId, activeTier],
  );

  const dynamicTiers = useMemo(() => {
    return Object.keys(tiers)
      .map((tierKey) => {
        const firstSetting = tiers[tierKey][0];

        const matchedLevel = levelSettings.find(
          (l) => l.tier.toLowerCase() === tierKey.toLowerCase(),
        );

        return {
          tier: tierKey,
          tierName: matchedLevel?.tierName || firstSetting?.tierName || tierKey,
          index: matchedLevel?.index ?? 999,
        };
      })
      .sort((a, b) => a.index - b.index);
  }, [tiers, levelSettings]);

  if (loading) {
    return (
      <Box
        className={styles.loadingWrapper}
        sx={{ display: 'flex', justifyContent: 'center', py: 8 }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (dynamicTiers.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="textSecondary">No tier settings found.</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.tabsContainer} sx={{ mb: 3 }}>
        <Tabs
          value={activeTier}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          className={styles.tierTabs}
        >
          {dynamicTiers.map(({ tier, tierName }) => (
            <Tab
              key={tier}
              label={tierName ? tierName.toUpperCase() : tier.toUpperCase()}
              value={tier}
            />
          ))}
        </Tabs>
      </Box>

      {activeTier && tiers[activeTier] && (
        <Box className={styles.gridWrapper}>
          <DataGrid
            rows={tiers[activeTier]}
            columns={columns}
            getRowId={(row) => row.id}
            sx={commonGridStyles}
            disableColumnMenu
            autoHeight
          />
        </Box>
      )}

      <EditBonusSettingModal
        open={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingSetting(null);
        }}
        setting={editingSetting}
        onSave={handleSaveEdit}
        saving={savingSetting}
      />
    </Box>
  );
};

export default BonusSettings;
